import backgroundTextureLight from 'assets/images/vxl/component/productIntroScreen/background-texture-light.png';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import CelebrationScreen from 'shared/patterns/vxl/CelebrationScreen/CelebrationScreen';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';

type PayOverTimeSuccessParams = {
  captionText: string;
  titleText: string;
  bodyText: string;
  infoMessage: string;
  isPaidOff: string;
};

const PayOverTimeSuccess = () => {
  const router = useRouter();
  const shouldNavigateHomeRef = useRef(false);
  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'manualRepay',
  });
  const {
    captionText,
    titleText,
    bodyText,
    infoMessage,
    isPaidOff: isPaidOffParam,
  } = useLocalSearchParams<PayOverTimeSuccessParams>();
  const { refreshTiles, fetching } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_BORROW,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_CHECKING,
    HomeTileTypeIds.INTERNAL_ACCOUNT_TILE_SAVINGS,
  ]);
  const isPaidOff = isPaidOffParam === 'true';
  const hasUrlParams = !!captionText && !!titleText && !!bodyText;
  const screenAnalyticsEventKey = isPaidOff
    ? '/pay-over-time/manual-payment/loan-paid-off'
    : '/pay-over-time/manual-payment/payment-received';

  useEffect(() => {
    if (!hasUrlParams) {
      router.navigate('/');
    }
  }, [hasUrlParams, router]);

  useEffect(() => {
    if (!shouldNavigateHomeRef.current) {
      return;
    }

    if (!fetching) {
      shouldNavigateHomeRef.current = false;
      router.dismissTo('/');
    }
  }, [fetching, router]);

  const handleButtonPress = () => {
    shouldNavigateHomeRef.current = true;
    refreshTiles();
  };

  useBackHandler(handleButtonPress);

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
          gestureEnabled: false,
        }}
      />
      {!isPaidOff ? (
        <SuccessScreen
          screenAnalyticsEventKey={screenAnalyticsEventKey}
          primaryButton={{
            label: translatedText('buttons.done'),
            isLoading: fetching,
            onPress: handleButtonPress,
          }}
          visual="CASH_A"
          eyebrowText={captionText}
          titleText={titleText}
          content={
            <View style={styles.content}>
              <BodyBaseText style={styles.message} testID="success-text">
                {bodyText}
              </BodyBaseText>
              <BodyXSText style={styles.description} testID="info-message">
                {infoMessage}
              </BodyXSText>
            </View>
          }
        />
      ) : (
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator
        >
          <CelebrationScreen
            screenAnalyticsEventKey={screenAnalyticsEventKey}
            illustrationKey="PAID_OFF_A_ANIMATED"
            illustrationAccessibilityLabel="You are all paid off celebration animation"
            backgroundTexture={backgroundTextureLight}
            bodyText={bodyText}
            captionText={infoMessage}
            button={{
              label: translatedText('buttons.done'),
              isLoading: fetching,
              onPress: handleButtonPress,
            }}
          />
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    gap: theme.spacing.m,
  },
  description: {
    color: theme.color.text.secondary,
    textAlign: 'center',
  },
  message: {
    textAlign: 'center',
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
  },
});

export default PayOverTimeSuccess;
